"use client";

import * as React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

interface AnalyticsProviderProps {
  posthogKey: string;
  posthogHost: string;
  /**
   * Authenticated user info, optional. When present we identify the
   * user with PostHog so subsequent events and any captured
   * exceptions are tied to their Supabase user id.
   */
  user?: { id: string; email: string | null; tier?: string } | null;
  children: React.ReactNode;
}

/**
 * App-side analytics + error capture.
 *
 * PostHog is the single SDK we boot here. With `capture_exceptions:
 * true`, the library installs `window.onerror` and
 * `unhandledrejection` listeners and ships exceptions to the same
 * project as our product events — no separate Sentry account.
 *
 * Server-side errors still surface in Vercel function logs; route
 * handlers that want to ship structured exceptions can post via
 * `posthog-node` (not wired here yet — add only if Vercel logs prove
 * insufficient).
 */
export function AnalyticsProvider({
  posthogKey,
  posthogHost,
  user,
  children,
}: AnalyticsProviderProps) {
  React.useEffect(() => {
    if (posthogKey && typeof window !== "undefined" && !posthog.__loaded) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        capture_pageview: false,
        capture_pageleave: true,
        // Auto-capture unhandled errors and promise rejections. Stack
        // traces are de-minified server-side by PostHog using the
        // browser source maps Next emits with
        // `productionBrowserSourceMaps: true`.
        capture_exceptions: true,
        persistence: "localStorage+cookie",
        loaded: (ph) => {
          ph.register({ app: "app" });
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Identify the user separately so we re-run when they change (sign
  // in / sign out / switch account flows).
  React.useEffect(() => {
    if (!user) return;
    if (posthogKey && typeof window !== "undefined" && posthog.__loaded) {
      posthog.identify(user.id, {
        email: user.email ?? undefined,
        tier: user.tier,
      });
    }
  }, [user, posthogKey]);

  return (
    <>
      {posthogKey ? <PostHogPageviews /> : null}
      {children}
    </>
  );
}

function PostHogPageviews() {
  return (
    <React.Suspense fallback={null}>
      <PageviewListener />
    </React.Suspense>
  );
}

function PageviewListener() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  React.useEffect(() => {
    if (!pathname) return;
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;
    posthog.capture("$pageview", {
      $current_url: window.location.origin + url,
    });
  }, [pathname, searchParams]);

  return null;
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  posthog.capture(event, properties);
}

/**
 * Manually report a caught exception to PostHog. Use for errors you
 * handle gracefully but still want surfaced (e.g. a Stripe call that
 * fell back to retry, an upload that failed but is queued).
 * Unhandled errors are captured automatically by `capture_exceptions`.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  posthog.captureException(error, context);
}

export function resetUser(): void {
  if (typeof window === "undefined") return;
  if (posthog.__loaded) posthog.reset();
}
